'use strict';

import Analytics from '../utils/Analytics';
import AnalyticsEvents from '../constants/AnalyticsEvents';
import AppDispatcher from '../AppDispatcher';
import ActionTypes from '../constants/ActionTypes';
import StampStore from '../stores/StampStore';
import ZineStore from '../stores/ZineStore';
import UserStore from '../stores/UserStore';
import ServiceAccountStore from '../stores/ServiceAccountStore';
import ServiceTypes from '../constants/ServiceTypes';
import SidebarActionCreators from '../actions/SidebarActionCreators';
import SidebarTypes from '../constants/SidebarTypes';
import getQueryUrl from '../utils/getQueryUrl';
import invariant from 'react/lib/invariant';
import isBestOf2014Special from '../utils/isBestOf2014Special';
import StampStatus from '../constants/StampStatus';

function openSharePopup(url) {
  var width = 500,
      height = 360,
      left = (screen.width / 2) - (width / 2),
      top = (screen.height / 2) - (height / 2);

  return window.open(url, '_blank',
    'status = 0, height = ' + height + ', width = ' + width +
    ', top=' + top + ', left=' + left
  );
}

function getHandleOrName(userId, service) {
  var user = UserStore.get(userId),
      account = ServiceAccountStore.getByType(user.id, service),
      handle = account && account.externalUsername;

  if (userId === 1 && service === ServiceTypes.TWITTER) {
    // Even if user has not been fully loaded yet, hardcode the handle.
    // Used on "Best of 2014" zine page sharing buttons.
    handle = 'HelloStampsy';
  }

  return handle ? ('@' + handle) : user.name;
}

function getShareStampText(stamp, service) {
  var stampTitle = stamp.title,
      handleOrName = getHandleOrName(stamp.user, service);

  switch (service) {
  case ServiceTypes.TWITTER:
    return 'Have you Seen “' + stampTitle + '” by ' + handleOrName;
  case ServiceTypes.TUMBLR:
    return 'Have you Seen “' + stampTitle + '” by ' + handleOrName + ' on https://stampsy.com';
  case ServiceTypes.PINTEREST:
    return '“' + stampTitle + '” by ' + handleOrName;
  default:
    throw new Error('Not implemented for', service);
  }
}

function getShareZineText(zine, service) {
  var zineTitle = zine.title,
      handleOrName = getHandleOrName(zine.curator, service);

  switch (service) {
  case ServiceTypes.TWITTER:
    return 'Check out “' + zineTitle + '” curated by ' + handleOrName;
  default:
    throw new Error('Not implemented for', service);
  }
}

function getShareUserText(userId, service) {
  var handleOrName = getHandleOrName(userId, service);

  switch (service) {
  case ServiceTypes.TWITTER:
    return 'Check out latest from ' + handleOrName + ' on Stampsy';
  default:
    throw new Error('Not implemented for', service);
  }
}

function getStampUrl(stamp) {
  switch (stamp.status) {
  case StampStatus.UNLISTED:
    return `https://stampsy.com/stamp/${stamp.uuid}-${stamp.id}`;
  default:
    return `https://stampsy.com/stamp/${stamp.id}`;
  }
}

var ShareActionCreators = {
  shareStamp({ stampId, service, imageUrl, source }) {
    var stamp = StampStore.get(stampId),
        url = getStampUrl(stamp),
        shareUrl;

    invariant(
      ServiceTypes.hasOwnProperty(service),
      'shareStamp() accepts a constant from ServiceTypes as service. You passed %s',
      service
    );

    if (service === ServiceTypes.PINTEREST && !imageUrl) {
      SidebarActionCreators.showSidebar(SidebarTypes.PINTEREST_PICKER, {
        stampId: stampId
      });
      return;
    }

    switch (service) {
    case ServiceTypes.TWITTER:
      shareUrl = getQueryUrl('//twitter.com/intent/tweet', {
        'related': 'hellostampsy',
        'text': getShareStampText(stamp, service),
        'url': url,
        'via': 'HelloStampsy'
      });
      break;
    case ServiceTypes.FACEBOOK:
      shareUrl = getQueryUrl('//www.facebook.com/share.php', {
        'u': url
      });
      break;
    case ServiceTypes.GOOGLE:
      shareUrl = getQueryUrl('//plus.google.com/share', {
        'url': url
      });
      break;
    case ServiceTypes.TUMBLR:
      shareUrl = getQueryUrl('//tumblr.com/share/photo', {
        'source': imageUrl || stamp.thumbnail.url,
        'caption': getShareStampText(stamp, service),
        'click_thru': url
      });
      break;
    case ServiceTypes.PINTEREST:
      shareUrl = getQueryUrl('//www.pinterest.com/pin/create/button/', {
        'url': url,
        'description': getShareStampText(stamp, service),
        'media': imageUrl
      });
      break;
    default:
      throw new Error('Unknown service');
    }

    openSharePopup(shareUrl);

    AppDispatcher.handleViewAction({
      type: ActionTypes.SHARE_STAMP,
      stampId: stampId,
      service: service,
      imageUrl: imageUrl,
      source: source
    });

    Analytics.trackGAEvent(AnalyticsEvents.STAMP_SHARE, source);
    Analytics.trackEvent(AnalyticsEvents.STAMP_SHARE, {
      stampId: stampId,
      service: service,
      imageUrl: imageUrl,
      source: source
    });
  },

  shareZine({ zineSlug, service, source }) {
    var zine = ZineStore.get(zineSlug),
        url = 'https://stampsy.com/' + zineSlug,
        shareUrl;

    invariant(
      ServiceTypes.hasOwnProperty(service),
      'shareZine() accepts a constant from ServiceTypes as service. You passed %s',
      service
    );

    switch (service) {
    case ServiceTypes.TWITTER:
      shareUrl = getQueryUrl('//twitter.com/intent/tweet', {
        'related': 'hellostampsy',
        'text': getShareZineText(zine, service),
        'url': url,
        'via': isBestOf2014Special(zine) ? null : 'HelloStampsy'
      });
      break;
    case ServiceTypes.FACEBOOK:
      shareUrl = getQueryUrl('//www.facebook.com/share.php', {
        'u': url
      });
      break;
    case ServiceTypes.GOOGLE:
      shareUrl = getQueryUrl('//plus.google.com/share', {
        'url': url
      });
      break;
    default:
      throw new Error('Unknown service');
    }

    openSharePopup(shareUrl);

    AppDispatcher.handleViewAction({
      type: ActionTypes.SHARE_ZINE,
      zineSlug: zineSlug,
      service: service,
      source: source
    });

    Analytics.trackGAEvent(AnalyticsEvents.ZINE_SHARE, source);
    Analytics.trackEvent(AnalyticsEvents.ZINE_SHARE, {
      source: source
    });
  },

  shareUser({ userId, service, source }) {
    var url = 'https://stampsy.com/user/' + userId,
        shareUrl;

    invariant(
      ServiceTypes.hasOwnProperty(service),
      'shareUser() accepts a constant from ServiceTypes as service. You passed %s',
      service
    );

    switch (service) {
    case ServiceTypes.TWITTER:
      shareUrl = getQueryUrl('//twitter.com/intent/tweet', {
        'related': 'hellostampsy',
        'text': getShareUserText(userId, service),
        'url': url,
        'via': 'HelloStampsy'
      });
      break;
    case ServiceTypes.FACEBOOK:
      shareUrl = getQueryUrl('//www.facebook.com/share.php', {
        'u': url
      });
      break;
    case ServiceTypes.GOOGLE:
      shareUrl = getQueryUrl('//plus.google.com/share', {
        'url': url
      });
      break;
    default:
      throw new Error('Unknown service');
    }

    openSharePopup(shareUrl);

    AppDispatcher.handleViewAction({
      type: ActionTypes.SHARE_USER,
      userId: userId,
      service: service,
      source: source
    });

    Analytics.trackGAEvent(AnalyticsEvents.USER_SHARE, source);
    Analytics.trackEvent(AnalyticsEvents.USER_SHARE, {
      source: source
    });
  },

  shareJoined({ service, source }) {
    var me = UserStore.getMe(),
        url = 'https://stampsy.com',
        shareUrl;

    invariant(
      ServiceTypes.hasOwnProperty(service),
      'shareJoined() accepts a constant from ServiceTypes as service. You passed %s',
      service
    );

    switch (service) {
    case ServiceTypes.TWITTER:
      shareUrl = getQueryUrl('//twitter.com/intent/tweet', {
        'related': 'hellostampsy',
        'text': 'I’ve just joined Stampsy — a sharing platform for visual stories and inspiration. Check it out',
        'url': url,
        'via': 'HelloStampsy'
      });
      break;
    case ServiceTypes.FACEBOOK:
      shareUrl = getQueryUrl('//www.facebook.com/share.php', {
        'u': url + '/?ref=' + me.id
      });
      break;
    case ServiceTypes.GOOGLE:
      shareUrl = getQueryUrl('//plus.google.com/share', {
        'url': url
      });
      break;
    default:
      throw new Error('Unknown service');
    }

    openSharePopup(shareUrl);

    AppDispatcher.handleViewAction({
      type: ActionTypes.SHARE_JOINED,
      service: service,
      source: source
    });

    Analytics.trackGAEvent(AnalyticsEvents.USER_SHARE, source);
    Analytics.trackEvent(AnalyticsEvents.USER_SHARE, {
      source: source
    });
  }
};

module.exports = ShareActionCreators;



/** WEBPACK FOOTER **
 ** ./actions/ShareActionCreators.js
 **/